import { Theme } from "@mui/material";

import { HIDE_DECIMAL, NO_SYMBOL, LabelType } from "@/hooks/useConfig";
import { ExtendedConfigData } from "@/hooks/useConfig/types";
import { formatPercentage } from "@/pages/scenario-builder/utils/formatPercentage";
import { OnYAxisClickFunction, ROIBreakdownEnum } from "@/shared/types";
import { calculatePercentage } from "@/shared/utils/calculatePercentage";
import { calculateROI } from "@/shared/utils/calculateROI";
import { isInsufficientSampleSize } from "@/Utils/dataFilters";
import { BCPLayer, Layer } from "@/Utils/hierarchy";

import { BreakdownDataProps } from "./chartUtils";
import { calculateLayerTotal } from "./utils/calculateLayerTotal";
import { getDisplayLabel, getLayerColor } from "./utils/displayUtils";

const ROWS_TO_SHOW = 15;

export const generateChartOptions = ({
  mappedData,
  chartLevel,
  muiTheme,
  config,
  label,
  totalRevenue,
  totalSpend,
  renderTooltip,
  onYAxisClick,
  legendStates,
}: {
  mappedData: BreakdownDataProps[];
  chartLevel: ROIBreakdownEnum;
  muiTheme: Theme;
  config: ExtendedConfigData;
  label: LabelType;
  totalRevenue: number;
  totalSpend: number;
  channelTotals: { [key: string]: { totalRevenue: number; totalSpend: number } };
  renderTooltip: any;
  onYAxisClick?: OnYAxisClickFunction;
  legendStates: Record<string, boolean>;
}) => {
  const formatCurrency = config.currency.formatCurrency;
  const barWidth = calculateBarWidth(mappedData.length);
  const legendData = getLegendData(chartLevel, mappedData, label, config);

  // Initialize legend states if empty
  const currentLegendStates =
    Object.keys(legendStates).length > 0 ? legendStates : initializeLegendStates(legendData);

  const startScrollValue = 100;
  const endScrollValue = startScrollValue - (ROWS_TO_SHOW / mappedData.length) * 100;

  const flexibleLegend = generateFlexibleLegend(mappedData, chartLevel, muiTheme, label, config);
  const legendConfig = Array.isArray(flexibleLegend)
    ? flexibleLegend.map((legend) => ({ ...legend, selected: currentLegendStates }))
    : { ...flexibleLegend, selected: currentLegendStates };

  return {
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      backgroundColor: muiTheme.palette.echartTooltip.background,
      textStyle: {
        color: muiTheme.palette.echartTooltip.textColor,
      },
      formatter: (params: any) => {
        return renderTooltip(params);
      },
    },
    grid: {
      left: "5%",
      right: "10%",
      bottom: "15%",
      top: "5%",
      containLabel: true,
    },
    xAxis: {
      type: "value",
      boundaryGap: [0, 0.01],
      splitLine: {
        show: true,
        lineStyle: {
          color: muiTheme.palette.ChartGuideLines.color,
        },
      },
      axisLabel: {
        color: muiTheme.palette.text.primary,
        formatter: (value: number) => {
          const formattedValue =
            chartLevel === ROIBreakdownEnum.SPEND_DOLLARS
              ? formatCurrency(value, HIDE_DECIMAL)
              : formatPercentage(value, true);

          return `{bold|${formattedValue}}`;
        },
        rich: {
          bold: {
            fontSize: (value: number) => (value > 100_000_000 ? "0.7rem" : "0.9rem"),
            fontFamily: "Montserrat",
          },
        },
      },
    },
    yAxis: {
      type: "category",
      data: mappedData.map((item) => item.mappedName),
      triggerEvent: !!onYAxisClick,
      axisLabel: {
        color: onYAxisClick ? muiTheme.palette.primary.main : muiTheme.palette.text.primary,
        fontSize: "0.95rem",
        fontWeight: onYAxisClick ? 600 : 400,
        formatter: (value: string) => {
          const maxLength = 30;
          return value.length > maxLength ? value.substring(0, maxLength) + "..." : value;
        },
        show: true,
        rich: {
          bold: {
            fontSize: "0.9rem",
            fontFamily: "Montserrat",
          },
        },
      },
      axisTick: {
        show: false,
      },
      axisLine: {
        show: true,
      },
      axisPointer: {
        show: !!onYAxisClick,
        type: "shadow",
      },
    },
    series:
      chartLevel === ROIBreakdownEnum.ROI
        ? generateROISeries({ mappedData, barWidth, muiTheme })
        : generateSeries(
            mappedData,
            chartLevel,
            muiTheme,
            totalRevenue,
            totalSpend,
            label,
            config,
            currentLegendStates
          ),
    legend: legendConfig,
    dataZoom: [
      {
        type: "slider",
        yAxisIndex: 0,
        start: startScrollValue,
        end: endScrollValue,
        handleSize: 0,
        backgroundColor: "rgba(0, 0, 0, 0.1)",
        fillerColor: "rgba(0, 0, 0, 0.25)",
        borderColor: "rgba(0, 0, 0, 0)",
        showDetail: false,
        showDataShadow: false,
        zoomLock: true,
        width: 0,
        handleIcon: "M50,0 L50,100 100,100 100,0 Z",
        throttle: 33,
        realtime: true,
      },
      {
        type: "inside",
        yAxisIndex: 0,
        start: startScrollValue,
        end: endScrollValue,
        zoomOnMouseWheel: false,
        moveOnMouseMove: true,
        moveOnMouseWheel: true,
        throttle: 33,
        realtime: true,
      },
    ],
    animation: true,
  };
};

const generateROISeries = ({
  mappedData,
  barWidth,
  muiTheme,
}: {
  mappedData: BreakdownDataProps[];
  barWidth: number;
  muiTheme: Theme;
}) => [
  {
    name: "Total Marketing ROI",
    type: "bar",
    stack: "roi",
    color: muiTheme.palette.roiGraph.layer.brand,
    barWidth: 2 * barWidth,
    data: mappedData.map((item) => {
      if (isInsufficientSampleSize(item)) {
        return {
          value: 0,
          itemStyle: {
            color: "transparent",
          },
          emphasis: {
            itemStyle: {
              color: "transparent",
            },
          },
        };
      }
      return calculateROI(
        Object.values(item.spend).reduce((acc, spend) => acc + (spend || 0), 0),
        Object.values(item.revenue).reduce((acc, revenue) => acc + (revenue || 0), 0)
      );
    }),
    label: {
      show: true,
      position: "right",
      color: muiTheme.palette.text.primary,
      offset: [10, 0],
      formatter: (params: any) => {
        const item = mappedData[params.dataIndex];
        if (isInsufficientSampleSize(item!)) {
          return "Insufficient Sample Size";
        }
        return formatPercentage(params.value, true);
      },
    },
  },
];

const generateSeries = (
  mappedData: BreakdownDataProps[],
  graphFilterLevel: ROIBreakdownEnum,
  muiTheme: Theme,
  totalRevenue: number,
  totalSpend: number,
  label: LabelType,
  config: ExtendedConfigData,
  legendStates: Record<string, boolean>
) => {
  const barWidth = calculateBarWidth(mappedData.length);
  const formatCurrency = config.currency.formatCurrency;
  const {
    config: { showReturnInsteadOfRevenue, revenueLabel },
  } = config;
  const revenueFormatterOptions = showReturnInsteadOfRevenue
    ? { ...NO_SYMBOL, ...HIDE_DECIMAL }
    : HIDE_DECIMAL;
  const layers: BCPLayer[] = [Layer.Brand, Layer.Category, Layer.Product];

  // Filter to only include layers that have data
  const layersWithData = layers.filter((_, index) => {
    const hasSpendData = mappedData.some((item) => (item.spend[index] ?? 0) > 0);
    const hasRevenueData = mappedData.some((item) => (item.revenue[index] ?? 0) > 0);
    return hasSpendData || hasRevenueData;
  });

  const lastVisibleSpendIndex = layersWithData
    .map((layer) => `${getDisplayLabel(layer, label)} Marketing Spend`)
    .reduce((lastIndex, legendName, index) => (legendStates[legendName] ? index : lastIndex), -1);

  const lastVisibleRevenueIndex = layersWithData
    .map((layer) => `${getDisplayLabel(layer, label)} Marketing ${revenueLabel}`)
    .reduce((lastIndex, legendName, index) => (legendStates[legendName] ? index : lastIndex), -1);

  switch (graphFilterLevel) {
    case ROIBreakdownEnum.SPEND_DOLLARS:
      return [
        // Spend
        ...layersWithData.map((layer, index) => {
          const originalIndex = layers.indexOf(layer);
          const legendName = `${getDisplayLabel(layer, label)} Marketing Spend`;
          return {
            name: legendName,
            type: "bar",
            stack: "spend",
            barWidth: barWidth,
            color: getLayerColor(layer, "spend", muiTheme),
            data: legendStates[legendName]
              ? mappedData.map((item) => item.spend[originalIndex])
              : mappedData.map(() => null),
            ...(index === lastVisibleSpendIndex && {
              label: {
                show: true,
                position: "right",
                color: muiTheme.palette.text.primary,
                formatter: (params: any) => {
                  if (params.dataIndex === undefined || !mappedData[params.dataIndex]) return "";
                  const data = mappedData[params.dataIndex] as BreakdownDataProps;
                  const enabledSpends = calculateLayerTotal(
                    data,
                    layersWithData,
                    layers,
                    legendStates,
                    label,
                    "spend",
                    revenueLabel
                  );
                  return enabledSpends === 0 ? "" : formatCurrency(enabledSpends, HIDE_DECIMAL);
                },
              },
            }),
          };
        }),
        // Revenue
        ...layersWithData.map((layer, index) => {
          const originalIndex = layers.indexOf(layer);
          const legendName = `${getDisplayLabel(layer, label)} Marketing ${revenueLabel}`;
          return {
            name: legendName,
            type: "bar",
            stack: "revenue",
            barWidth: barWidth,
            color: getLayerColor(layer, "revenue", muiTheme),
            data: legendStates[legendName]
              ? mappedData.map((item) => item.revenue[originalIndex])
              : mappedData.map(() => null),
            ...(index === lastVisibleRevenueIndex && {
              label: {
                show: true,
                position: "right",
                color: muiTheme.palette.text.primary,
                formatter: (params: any) => {
                  if (params.dataIndex === undefined || !mappedData[params.dataIndex]) return "";
                  const data = mappedData[params.dataIndex] as BreakdownDataProps;
                  const enabledRevenues = calculateLayerTotal(
                    data,
                    layersWithData,
                    layers,
                    legendStates,
                    label,
                    "revenue",
                    revenueLabel
                  );
                  return enabledRevenues === 0
                    ? ""
                    : formatCurrency(enabledRevenues, revenueFormatterOptions);
                },
              },
            }),
          };
        }),
      ];
    case ROIBreakdownEnum.SPEND_PERCENTAGE:
      return [
        // Spend
        ...layersWithData.map((layer, index) => {
          const originalIndex = layers.indexOf(layer);
          const legendName = `${getDisplayLabel(layer, label)} Marketing Spend`;
          return {
            name: legendName,
            type: "bar",
            stack: "spend",
            barWidth: barWidth,
            color: getLayerColor(layer, "spend", muiTheme),
            data: legendStates[legendName]
              ? mappedData.map((item) => calculatePercentage(item.spend[originalIndex], totalSpend))
              : mappedData.map(() => null),
            ...(index === lastVisibleSpendIndex && {
              label: {
                show: true,
                position: "right",
                color: muiTheme.palette.text.primary,
                formatter: (params: any) => {
                  if (params.dataIndex === undefined || !mappedData[params.dataIndex]) return "";
                  const data = mappedData[params.dataIndex] as BreakdownDataProps;
                  const enabledSpends = calculateLayerTotal(
                    data,
                    layersWithData,
                    layers,
                    legendStates,
                    label,
                    "spend",
                    revenueLabel
                  );
                  return enabledSpends === 0
                    ? ""
                    : formatPercentage((100 * enabledSpends) / totalSpend, true);
                },
              },
            }),
          };
        }),
        // Revenue
        ...layersWithData.map((layer, index) => {
          const originalIndex = layers.indexOf(layer);
          const legendName = `${getDisplayLabel(layer, label)} Marketing ${revenueLabel}`;
          return {
            name: legendName,
            type: "bar",
            stack: "revenue",
            barWidth: barWidth,
            color: getLayerColor(layer, "revenue", muiTheme),
            data: legendStates[legendName]
              ? mappedData.map((item) =>
                  calculatePercentage(item.revenue[originalIndex], totalRevenue)
                )
              : mappedData.map(() => null),
            ...(index === lastVisibleRevenueIndex && {
              label: {
                show: true,
                position: "right",
                color: muiTheme.palette.text.primary,
                formatter: (params: any) => {
                  if (params.dataIndex === undefined || !mappedData[params.dataIndex]) return "";
                  const data = mappedData[params.dataIndex] as BreakdownDataProps;
                  const enabledRevenues = calculateLayerTotal(
                    data,
                    layersWithData,
                    layers,
                    legendStates,
                    label,
                    "revenue",
                    revenueLabel
                  );
                  return enabledRevenues === 0
                    ? ""
                    : formatPercentage((100 * enabledRevenues) / totalRevenue, true);
                },
              },
            }),
          };
        }),
      ];
    default:
      throw new Error("Invalid chart level");
  }
};

const initializeLegendStates = (legendNames: string[]): Record<string, boolean> => {
  return legendNames.reduce(
    (acc, name) => {
      acc[name] = true; // All legends start enabled by default
      return acc;
    },
    {} as Record<string, boolean>
  );
};

const generateFlexibleLegend = (
  mappedData: BreakdownDataProps[],
  graphFilterLevel: ROIBreakdownEnum,
  muiTheme: Theme,
  label: LabelType,
  config: ExtendedConfigData
) => {
  const legendData = getLegendData(graphFilterLevel, mappedData, label, config);
  const {
    config: { showReturnInsteadOfRevenue },
  } = config;
  const revenueLabel = showReturnInsteadOfRevenue ? "Return" : "Revenue";
  const textStyle = {
    color: muiTheme.palette.text.primary,
    fontWeight: 600,
    fontSize: 14,
    fontFamily: "Montserrat",
  };

  const itemWidth = 20;
  const itemGap = 10;

  if (graphFilterLevel === ROIBreakdownEnum.ROI) {
    return {
      orient: "horizontal",
      bottom: 0,
      left: "center",
      itemWidth: itemWidth,
      itemGap: itemGap,
      textStyle: textStyle,
      data: legendData,
      padding: [5, 10],
      itemStyle: {
        borderWidth: 0,
      },
    };
  }

  const spendLegends = legendData.filter((item) => item.includes("Spend"));
  const revenueLegends = legendData.filter((item) => item.includes(revenueLabel));

  return [
    {
      orient: "vertical",
      bottom: 0,
      left: "20%",
      itemWidth: itemWidth,
      itemGap: itemGap,
      textStyle: textStyle,
      data: spendLegends,
      padding: [5, 10],
      itemStyle: {
        borderWidth: 0,
      },
    },
    {
      orient: "vertical",
      bottom: 0,
      left: "50%",
      itemWidth: itemWidth,
      itemGap: itemGap,
      textStyle: textStyle,
      data: revenueLegends,
      padding: [5, 10],
      itemStyle: {
        borderWidth: 0,
      },
    },
  ];
};

const getLegendData = (
  graphFilterLevel: ROIBreakdownEnum,
  mappedData: BreakdownDataProps[],
  label: LabelType,
  config: ExtendedConfigData
): string[] => {
  const {
    config: { showReturnInsteadOfRevenue },
  } = config;
  const revenueLabel = showReturnInsteadOfRevenue ? "Return" : "Revenue";
  switch (graphFilterLevel) {
    case ROIBreakdownEnum.SPEND_DOLLARS:
    case ROIBreakdownEnum.SPEND_PERCENTAGE:
      return [
        ...generateLabels(ROIBreakdownEnum.SPEND_DOLLARS, "Spend", mappedData, label),
        ...generateLabels(ROIBreakdownEnum.SPEND_DOLLARS, revenueLabel, mappedData, label),
      ];
    case ROIBreakdownEnum.ROI:
      return ["Total Marketing ROI"];
    default:
      throw new Error("Invalid chart level");
  }
};

const generateLabels = (
  breakdownType: ROIBreakdownEnum,
  type: string,
  mappedData: BreakdownDataProps[],
  label: LabelType
) => {
  const layers = [
    { key: Layer.Brand, label: getDisplayLabel(Layer.Brand, label) },
    { key: Layer.Category, label: getDisplayLabel(Layer.Category, label) },
    { key: Layer.Product, label: getDisplayLabel(Layer.Product, label) },
  ];

  if (
    breakdownType === ROIBreakdownEnum.SPEND_DOLLARS ||
    breakdownType === ROIBreakdownEnum.SPEND_PERCENTAGE
  ) {
    return layers.reduce((filteredLabels: string[], layer, index) => {
      // Check if ANY item has non-zero data for this specific layer
      const hasData = mappedData.some((data) => {
        const value = ["Revenue", "Return"].includes(type)
          ? data.revenue[index]
          : data.spend[index];
        return value !== null && value !== undefined && value > 0;
      });

      if (hasData) {
        filteredLabels.push(`${layer.label} Marketing ${type}`);
      }
      return filteredLabels;
    }, []);
  } else {
    return layers.reduce((filteredLabels: string[], layer) => {
      const hasData = mappedData.some((data) => {
        return isValidValue(data.revenue) && isValidValue(data.spend);
      });

      if (hasData) {
        filteredLabels.push(`${layer.label} Marketing`);
      }
      return filteredLabels;
    }, []);
  }
};

const isValidValue = (value: number | number[] | null | undefined) => {
  return value !== null && value !== undefined && value !== 0;
};

const calculateBarWidth = (dataLength: number) => {
  const maxBarWidth = 30;
  return Math.max(10, maxBarWidth - dataLength);
};
